<template>
  <div class="container-fluid">
    <div v-if="isPurchaseFormActive">
      <purchase-approval-form
        :item="order"
        @saved="onCloseCallOut"
        @close="
          () => {
            activeForm = null;
          }
        "
      />
    </div>
    <page-header
      :page="header.page"
      :actions="header.actions"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <request-to-purchase
      v-if="showRequestToPurchase"
      :order-id="orderId"
      @close="onRequestToPurchaseClosed"
    />
    <board class="my-3" :details="boardDetails">
      <template #data-item="{ item }">
        <div v-if="!item.data">
          <custom-balloon
            :background-color="item.statusColor"
            :text="item.statusName"
            dot
          />
        </div>
      </template>
    </board>
    <div v-if="orderSearched" class="my-2">
      <q-tab :active-tab="activeTab">
        <q-tab-item
          lazy
          :tabindex="0"
          title="Disponible en CSEE/ORE"
          @click="setCurrentTab(0)"
        />
        <q-tab-item
          lazy
          :tabindex="1"
          title="Fuera de CSEE/ORE"
          @click="setCurrentTab(1)"
        />
      </q-tab>
      <materials-and-equipments-search
        :is-out-of-center="isOutOfCenter"
        :order="order"
        @refresh="refresh"
      />
    </div>
  </div>
</template>
<script>
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomBalloon from 'custom-balloon';
import MaterialsAndEquipmentsSearch from './components/MaterialsAndEquipmentsSearch.vue';
import { studentBookStatus as studentMaterialStatus } from '@/utils/constants/book/studentBookStatus.js';
import PurchaseApprovalForm from './components/PurchaseApprovalForm.vue';
import RequestToPurchase from './components/RequestToPurchase.vue';
import { QTab, QTabItem } from '@/components/common/q-tabs';

import service from '@/services/api/inventoryOrderMaterialsAndEquipments.api';

import storeMixin from '@/mixin/storeGenerator';
import alertMixin from '@/mixin/alert.mixin';

const TAB_ITEMS = {
  IN: 0,
  OUT: 1,
};
const TEMPLATE_ACTION_INDEX = {
  INFO: 0,
  REJECTREQUEST: 1,
  DELIVERTOSCHOOL: 2,
  RECIVEDBYMASTER: 3,
  CHECKASSIGNEDBOOK: 4,
  APPROVEPURCHASE: 5,
  CONFIRMPURCHASE: 7,
  DELIVERYTOCSEE: 8,
};

export default {
  name: 'InventoryRequestsMaterialsAndEquipments',
  mixins: [storeMixin, alertMixin],
  components: {
    PageHeader,
    Board,
    CustomBalloon,
    QTab,
    QTabItem,
    MaterialsAndEquipmentsSearch,
    RequestToPurchase,
    PurchaseApprovalForm,
  },
  data() {
    return {
      order: {},
      orderSearched: false,
      showRequestToPurchase: false,
      activeTab: Number(this.$route.query.tab ?? TAB_ITEMS.IN),
      activeForm: null,
    };
  },
  async mounted() {
    this.activeTab = this.tabParam;
    this.getOrder();
  },
  computed: {
    isPurchaseFormActive() {
      return this.activeForm == TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
    },
    orderId() {
      return Number(this.$route.params.id);
    },
    tabParam() {
      return Number(this.$route.query.tab ?? TAB_ITEMS.IN);
    },
    header() {
      return {
        page: {
          icon: 'fas fa-list-ul',
          title: `Manejo de Disponibilidad: ${this.order?.material?.name}`,
          routes: [
            'Inicio',
            'Materiales',
            'Solicitud de Materiales',
            `Manejo de Disponibilidad`,
          ],
        },
        actions: [
          {
            text: 'Regresar',
            icon: 'fas fa-arrow-circle-left',
            variant: 'outline-success',
            click: () => this.$router.go(-1),
          },
          {
            text: 'Solicitar para Compra',
            icon: 'far fa-plus-circle',
            variant: 'success',
            visible:
              !this.showRequestToPurchase &&
              !this.order?.hasPurchases &&
              !this.order?.itemId &&
              this.order.status?.id ==
                studentMaterialStatus.PedienteDeterminacionCSEE,
            click: () => (this.showRequestToPurchase = true),
          },
          {
            text: 'Aprobar Compra',
            icon: 'far fa-plus-circle',
            variant: 'success',
            visible: this.isPurchaseApprovalButtonVisible,
            click: () => {
              this.activeForm = TEMPLATE_ACTION_INDEX.APPROVEPURCHASE;
            },
          },
        ],
      };
    },
    isPurchaseApprovalButtonVisible() {
      return (
        this.order.status?.id ==
          studentMaterialStatus.PedienteDeterminacionSAEE &&
        this.userHasPermissions(
          'academic.materialsandequipments.requests.approvepurchase'
        )
      );
    },
    boardDetails() {
      return [
        [
          {
            label: 'Estatus:',
            statusColor: this.order.statusColor,
            statusName: this.order.status?.name,
            cols: 'col-12 col-md-4 pt-2',
          },
          {
            label: 'Número de Solictud:',
            data: this.order.requestCode ?? '-',
            cols: 'col-12 col-md-2 pt-2',
          },
          {
            label: 'Nombre del Material/Equipo:',
            data: this.order.material?.name ?? '-',
            cols: 'col-12 col-md-3 pt-2',
          },
          {
            label: 'Categoría:',
            data: this.order.category?.name ?? '-',
            cols: 'col-12 col-md-3 pt-2',
          },
          {
            label: 'Tipo de Salón:',
            data: this.order.saloonType?.name ?? '-',
            cols: 'col-12 col-md-4 pt-2',
          },
          {
            label: 'Centro:',
            data: this.order.center?.name ?? '-',
            cols: 'col-12 col-md-2 pt-2',
          },
          {
            label: 'Escuela:',
            data: this.order.school?.name ?? '-',
            cols: 'col-12 col-md-6 pt-2',
          },
        ],
      ];
    },
    isOutOfCenter() {
      return this.activeTab === TAB_ITEMS.OUT;
    },
  },
  methods: {
    async getOrder() {
      const { data } = await service.get(this.orderId);
      this.order = data;
      this.orderSearched = true;
    },
    onRequestToPurchaseClosed(submitted) {
      this.showRequestToPurchase = false;
      if (submitted) this.$router.go(-1);
    },
    async onCloseCallOut() {
      this.activeForm = null;
      await this.getOrder();
    },
    setCurrentTab(tab) {
      const sameRoute = this.tabParam === tab;
      if (sameRoute) return;

      this.activeTab = tab;
      if (tab) this.$router.replace({ query: { tab } });
      else this.$router.replace({ name: this.$route.name });
    },
    async refresh() {
      await this.getOrder();
    },
  },
};
</script>
<style scoped></style>
